import { useState, useEffect, useCallback, useRef } from 'react';
import { IS_CAPACITOR } from '../api/config';
import {
  getNotificationPrefs,
  updateNotificationPrefs,
  sendTestNotification,
} from '../api/authClient';
import { requestAndRegister, getPermissionStatus } from '../api/pushClient';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { useAppleMusicAuth } from '../hooks/useAppleMusicAuth';
import { DEFAULT_MUSIC_SERVICE } from '../utils/exportUtils';
import '../styles/auth.css';
import '../styles/notifications.css';

const LOAD_TIMEOUT_MS = 12000;

// fetch() has no built-in timeout, so a black-holed request would spin forever.
// Race it against a timer so the UI always resolves to a visible state.
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

function NotificationSettingsModal({ onClose }) {
  const { user } = useAuth();
  const { setMusicService } = useAuthActions();
  const musicService = user?.musicService || DEFAULT_MUSIC_SERVICE;
  const appleMusic = useAppleMusicAuth(musicService);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState('weekly');
  const [pushConfigured, setPushConfigured] = useState(true);
  const [permission, setPermission] = useState('prompt');
  const [saving, setSaving] = useState(false);
  const [testMsg, setTestMsg] = useState('');

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async () => {
    if (mountedRef.current) { setLoading(true); setLoadError(''); }
    try {
      const res = await withTimeout(getNotificationPrefs(), LOAD_TIMEOUT_MS);
      const data = await res.json();
      if (mountedRef.current) {
        setEnabled(data.recommendation?.enabled || false);
        setFrequency(data.recommendation?.frequency || 'weekly');
        setPushConfigured(data.pushConfigured !== false);
      }
    } catch (err) {
      if (mountedRef.current) {
        setLoadError(
          err && err.message === 'timeout'
            ? 'Timed out reaching the server.'
            : 'Couldn’t load notification settings.'
        );
      }
    }
    try {
      if (IS_CAPACITOR) {
        const status = await withTimeout(getPermissionStatus(), LOAD_TIMEOUT_MS);
        if (mountedRef.current) setPermission(status);
      }
    } catch {
      if (mountedRef.current) setPermission('unavailable');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async (nextEnabled, nextFrequency) => {
    setSaving(true);
    try {
      await updateNotificationPrefs('recommendation', nextEnabled, nextFrequency);
    } catch {
      /* best-effort */
    } finally {
      setSaving(false);
    }
  }, []);

  const handleToggle = async () => {
    if (saving) return;
    const next = !enabled;
    if (next && IS_CAPACITOR && permission !== 'granted') {
      const status = await requestAndRegister();
      setPermission(status);
      if (status !== 'granted') {
        // OS denied — leave the toggle off and let the note guide them.
        return;
      }
    }
    setEnabled(next);
    persist(next, frequency);
  };

  const handleFrequency = (value) => {
    if (value === frequency) return;
    setFrequency(value);
    if (enabled) persist(enabled, value);
  };

  const handleTest = async () => {
    setTestMsg('');
    try {
      const res = await sendTestNotification();
      const data = await res.json();
      setTestMsg(data.ok ? 'Sent — check your device.' : (data.error || 'Could not send.'));
    } catch {
      setTestMsg('Could not send.');
    }
  };

  const osDenied = IS_CAPACITOR && permission === 'denied';
  const unavailable = IS_CAPACITOR && permission === 'unavailable';
  const controlsDisabled = saving || !IS_CAPACITOR || !pushConfigured || unavailable;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2 className="auth-modal-title">Settings</h2>

        <div className="notif-section">
          <div className="notif-row">
            <div className="notif-row-text">
              <span className="notif-row-label">Music service</span>
              <span className="notif-row-desc">Where song links open.</span>
            </div>
            <div className="notif-segmented">
              <button
                className={`notif-seg-btn ${musicService === 'spotify' ? 'active' : ''}`}
                onClick={() => setMusicService('spotify')}
              >
                Spotify
              </button>
              <button
                className={`notif-seg-btn ${musicService === 'apple-music' ? 'active' : ''}`}
                onClick={() => setMusicService('apple-music')}
              >
                Apple Music
              </button>
            </div>
          </div>

          {musicService === 'apple-music' && appleMusic.configured && (
            <div className="notif-row">
              <div className="notif-row-text">
                <span className="notif-row-label">
                  Apple Music library
                  {appleMusic.isAuthorized && (
                    <span className="notif-am-connected"> · Connected</span>
                  )}
                </span>
                <span className="notif-row-desc">
                  {appleMusic.isAuthorized
                    ? 'Playlists can be exported straight to your library.'
                    : 'Connect to export playlists straight to your library.'}
                </span>
              </div>
              {appleMusic.isAuthorized ? (
                <button className="notif-test-btn" onClick={appleMusic.disconnect}>
                  Disconnect
                </button>
              ) : (
                <button
                  className="notif-test-btn"
                  onClick={appleMusic.connect}
                  disabled={appleMusic.connecting}
                >
                  {appleMusic.connecting ? 'Connecting…' : 'Connect'}
                </button>
              )}
            </div>
          )}
          {musicService === 'apple-music' && appleMusic.error && (
            <div className="notif-note">{appleMusic.error}</div>
          )}
        </div>

        <h3 className="notif-section-heading">Notifications</h3>

        {loading ? (
          <p className="notif-row-desc">Loading…</p>
        ) : (
          <div className="notif-section">
            {loadError && (
              <div className="notif-note">
                {loadError}
                <button className="notif-test-btn" onClick={load} style={{ marginTop: 10 }}>
                  Retry
                </button>
              </div>
            )}

            <div className="notif-row">
              <div className="notif-row-text">
                <span className="notif-row-label">Recommendations</span>
                <span className="notif-row-desc">
                  A pick from your universe and who it's connected to.
                </span>
              </div>
              <button
                className={`notif-switch ${enabled ? 'on' : ''}`}
                onClick={handleToggle}
                disabled={controlsDisabled}
                aria-label="Toggle recommendation notifications"
              />
            </div>

            {enabled && (
              <div className="notif-row">
                <div className="notif-row-text">
                  <span className="notif-row-label">Frequency</span>
                </div>
                <div className="notif-segmented">
                  <button
                    className={`notif-seg-btn ${frequency === 'weekly' ? 'active' : ''}`}
                    onClick={() => handleFrequency('weekly')}
                    disabled={controlsDisabled}
                  >
                    Weekly
                  </button>
                  <button
                    className={`notif-seg-btn ${frequency === 'daily' ? 'active' : ''}`}
                    onClick={() => handleFrequency('daily')}
                    disabled={controlsDisabled}
                  >
                    Daily
                  </button>
                </div>
              </div>
            )}

            {!IS_CAPACITOR && (
              <div className="notif-note">
                Push notifications are available in the Stellar iOS app.
              </div>
            )}

            {unavailable && (
              <div className="notif-note">
                Notifications aren't available in this build yet. Please update to
                the latest version of Stellar.
              </div>
            )}

            {IS_CAPACITOR && !unavailable && !pushConfigured && (
              <div className="notif-note">
                Notifications aren't available yet. Please try again later.
              </div>
            )}

            {osDenied && (
              <div className="notif-note">
                Notifications are turned off for Stellar in iOS Settings. Enable them in
                Settings › Notifications › Stellar to receive recommendations.
              </div>
            )}

            {IS_CAPACITOR && pushConfigured && enabled && permission === 'granted' && (
              <>
                <button className="notif-test-btn" onClick={handleTest} disabled={saving}>
                  Send a test notification
                </button>
                {testMsg && <span className="notif-row-desc">{testMsg}</span>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationSettingsModal;
